import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaDownload, 
  FaChevronLeft, 
  FaChevronRight, 
  FaSearch, 
  FaFilter, 
  FaTimes, 
  FaUserEdit, 
  FaExchangeAlt, 
  FaMapMarkerAlt, 
  FaUserFriends, 
  FaListUl,
  FaIdCard
} from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './CustomerManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import EditCustomerModal from './EditCustomerModal';
import ChangeCustomerState from './ChangeCustomerState';
import AuthorizedPersonnelModal from './AuthorizedPersonnelModal';
import CustomerTariffsModal from './CustomerTariffsModal';
import CustomerAddressModal from './CustomerAddressModal';
import CustomerCardModal from './CustomerCardModal';
import TableFilter from '../../components/TableFilter';
import statusVideo from '../../assets/HowToVideos/aktif-inaktif.mp4';
import customerEditVideo from '../../assets/HowToVideos/customer_edit.mp4';
import authorizedPersonnelVideo from '../../assets/HowToVideos/customer_yetkililer.mp4';
import tariffsVideo from '../../assets/HowToVideos/customer_tarifeler.mp4';
import { useTheme } from '../../contexts/ThemeContext';
import './CustomerManagementDarkMode.css';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const CustomerManagementPage = () => {
  const { isDarkMode } = useTheme();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isAuthorizedPersonnelModalOpen, setIsAuthorizedPersonnelModalOpen] = useState(false);
  const [isCustomerTariffsModalOpen, setIsCustomerTariffsModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [filters, setFilters] = useState({ 'Durum': 'aktif' });
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('');
  const [resizingColumn, setResizingColumn] = useState(null);
  const [columnWidths, setColumnWidths] = useState({});
  const tableRef = useRef(null);
  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  // Pagination states - sayfalama tamamen istemci tarafında yapılır
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10); // Sayfa başına gösterilen müşteri sayısı
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Filtering states
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [allCustomers, setAllCustomers] = useState([]);
  const [statsData, setStatsData] = useState({ active: 0, inactive: 0 });
  const [visibleColumns, setVisibleColumns] = useState({
    'Başlık': true,
    'Bilgi': true,
    'Durum': true,
    'Ana Anlaşma Tarihi': true,
    'Ana Anlaşma Numarası': true,
    'Döviz Tipi': false,
    'Müşteri Hizmet Num.': false,
    'Teslim Tarihi': false,
    'Vergi Numarası': true,
    'Yurtiçi mi?': false,
    'Ödeme Periyodu': false
  });

  const columns = {
    'Başlık': { label: 'Başlık', type: 'text' },
    'Bilgi': { label: 'Bilgi', type: 'text' },
    'Durum': { 
      label: 'Durum', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "aktif", label: "Aktif" },
        { value: "inaktif", label: "İnaktif" }
      ]
    },
    'Ana Anlaşma Tarihi': { label: 'Ana Anlaşma Tarihi', type: 'date' },
    'Ana Anlaşma Numarası': { label: 'Ana Anlaşma Numarası', type: 'text' },
    'Döviz Tipi': { 
      label: 'Döviz Tipi', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "TRY", label: "TRY" },
        { value: "USD", label: "USD" },
        { value: "EUR", label: "EUR" },
        { value: "DKK", label: "DKK" }
      ]
    },
    'Müşteri Hizmet Num.': { label: 'Müşteri Hizmet Numarası', type: 'text' },
    'Teslim Tarihi': { label: 'Teslim Tarihi', type: 'date' },
    'Vergi Numarası': { label: 'Vergi Numarası', type: 'text' },
    'Yurtiçi mi?': { 
      label: 'Yurtiçi mi?', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Evet", label: "Evet" },
        { value: "Hayır", label: "Hayır" }
      ]
    },
    'Ödeme Periyodu': { 
      label: 'Ödeme Periyodu', 
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "Aylık", label: "Aylık" },
        { value: "3 Aylık", label: "3 Aylık" },
        { value: "6 Aylık", label: "6 Aylık" },
        { value: "Yıllık", label: "Yıllık" }
      ]
    }
  };

  // Müşteri adı hem 'Başlık' hem de 'Müşteri Adı' alanında tutulabiliyor;
  // eski kayıtlarda 'Başlık' hiç yazılmamış olabilir
  const resolveTitle = (customer) => customer['Başlık'] || customer['Müşteri Adı'] || '';

  // Tüm müşterileri tek seferde çeker.
  // NOT: Burada bilerek orderBy KULLANILMIYOR - Firestore orderBy'a verilen alanı
  // içermeyen dokümanları sonuçtan tamamen düşürüyor ve 'Başlık' alanı olmayan
  // müşteriler tablodan kayboluyordu. Sıralama istemci tarafında yapılıyor.
  const fetchAllCustomers = async () => {
    setIsLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'Customers'));
      const customersData = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          // Tabloda ve aramada tutarlı bir ad alanı olması için normalize edilir
          'Başlık': data['Başlık'] || data['Müşteri Adı'] || '',
        };
      });

      customersData.sort((a, b) =>
        resolveTitle(a).localeCompare(resolveTitle(b), 'tr', { sensitivity: 'base' })
      );

      setAllCustomers(customersData);
      setTotalCustomers(customersData.length);
      setStatsData({
        active: customersData.filter((c) => c['Durum']?.toLowerCase() === 'aktif').length,
        inactive: customersData.filter((c) => c['Durum']?.toLowerCase() === 'inaktif').length,
      });

      return customersData;
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Müşteriler yüklenirken hata oluştu');
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const refreshCustomers = () => {
    setCurrentPage(1);
    fetchAllCustomers();
  };

  useEffect(() => {
    // Eski sürümden kalan sayfa önbelleği artık kullanılmıyor
    try {
      localStorage.removeItem('customersPageCache');
    } catch (e) {
      // sessizce yoksay
    }

    fetchAllCustomers();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    // Check if any filters or search are active (with minimum 3 characters for search)
    const hasActiveSearch = searchTerm && searchTerm.trim().length >= 3;
    const hasActiveFilters = Object.keys(filters).some(key => {
      // Durum filter with value 'aktif' is default, so don't count it as active filter
      if (key === 'Durum' && filters[key] === 'aktif') return false;
      // For text filters, require at least 3 characters
      if (filters[key] && typeof filters[key] === 'string' &&
          key !== 'Durum' && key !== 'Döviz Tipi' && key !== 'Yurtiçi mi?' && key !== 'Ödeme Periyodu') {
        return filters[key].trim().length >= 3;
      }
      return filters[key] && filters[key] !== '';
    });

    const isAnyFilterActive = hasActiveSearch || hasActiveFilters;
    setIsFilterActive(isAnyFilterActive);

    // Kaynak her zaman tüm müşteri listesidir; filtreleme ve sayfalama
    // aynı veri kümesi üzerinde çalışır
    let filtered = allCustomers;

    // Apply search term first (minimum 3 characters)
    if (searchTerm && searchTerm.trim().length >= 3) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        customer =>
          (customer['Başlık'] && customer['Başlık'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (customer['Bilgi'] && customer['Bilgi'].toString().toLowerCase().includes(lowercasedSearch)) ||
          (customer['Vergi Numarası'] && customer['Vergi Numarası'].toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        switch (key) {
          case 'Durum':
            // Durum filter always applies (no minimum length)
            filtered = filtered.filter(customer =>
              customer[key]?.toLowerCase() === filters[key].toLowerCase()
            );
            break;
          case 'Döviz Tipi':
          case 'Yurtiçi mi?':
          case 'Ödeme Periyodu':
            // Select filters apply immediately
            filtered = filtered.filter(customer =>
              customer[key] === filters[key]
            );
            break;
          case 'Ana Anlaşma Tarihi':
          case 'Teslim Tarihi':
            // Date filters apply immediately
            filtered = filtered.filter(customer =>
              customer[key]?.includes(filters[key])
            );
            break;
          default:
            // Text filters require minimum 3 characters
            const filterValue = String(filters[key]);
            if (filterValue.trim().length >= 3) {
              filtered = filtered.filter(customer =>
                String(customer[key])
                  ?.toLowerCase()
                  .includes(filterValue.toLowerCase())
              );
            }
        }
      }
    });

    setFilteredCustomers(filtered);
  }, [allCustomers, filters, searchTerm]);

  // Filtre veya arama değiştiğinde geçerli sayfa dışına düşmemek için başa dön
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Use stats data fetched separately for accurate counts
  const activeCount = statsData.active;
  const inactiveCount = statsData.inactive;

  // Sayfalama, filtrelenmiş sonuç kümesi üzerinden hesaplanır; böylece
  // "Sayfa X / Y" bilgisi tabloda gerçekten görünen kayıtlarla örtüşür
  const totalPages = Math.max(1, Math.ceil(filteredCustomers.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pagedCustomers = filteredCustomers.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  // Pagination handlers
  const goToNextPage = () => {
    if (safePage < totalPages) setCurrentPage(safePage + 1);
  };

  const goToPreviousPage = () => {
    if (safePage > 1) setCurrentPage(safePage - 1);
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const downloadExcel = async () => {
    try {
      // Ekranda kullanılan (eksiksiz) liste dışa aktarılır
      toast.info('Excel dosyası hazırlanıyor...');
      const exportCustomers = allCustomers.length > 0 ? allCustomers : await fetchAllCustomers();

      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Müşteriler');

      // Add only visible columns to the Excel sheet
      const visibleColumnKeys = Object.keys(visibleColumns).filter(key => visibleColumns[key]);

      worksheet.columns = visibleColumnKeys.map(key => ({
        header: columns[key].label,
        key: key.replace(/\s/g, '_').replace(/\./g, ''),
        width: 20
      }));

      exportCustomers.forEach(customer => {
        const rowData = {};
        visibleColumnKeys.forEach(key => {
          rowData[key.replace(/\s/g, '_').replace(/\./g, '')] = truncateText(customer[key]);
        });
        worksheet.addRow(rowData);
      });

      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFE5E7EB' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF111827' }
        };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });

      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Müşteriler_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel dosyası başarıyla indirildi');
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Error downloading file');
    }
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
  };

  const handleEditClick = () => {
    if (selectedCustomer) setIsModalOpen(true);
    else toast.error('Lütfen düzenlemek için bir müşteri seçin.');
  };

  const handleCardClick = () => {
    if (selectedCustomer) setIsCardModalOpen(true);
    else toast.error('Lütfen bir müşteri seçin.');
  };

  const handleStatusChangeClick = () => {
    if (selectedCustomer) setIsStatusModalOpen(true);
    else toast.error('Lütfen bir müşteri seçin.');
  };

  const handleAuthorizedPersonnelClick = () => {
    if (selectedCustomer) setIsAuthorizedPersonnelModalOpen(true);
    else toast.error('Lütfen bir müşteri seçin.');
  };

  const handleTariffsClick = () => {
    if (selectedCustomer) setIsCustomerTariffsModalOpen(true);
    else toast.error('Lütfen bir müşteri seçin.');
  };

  const handleAddressClick = () => {
    if (selectedCustomer) setIsAddressModalOpen(true);
    else toast.error('Lütfen bir müşteri seçin.');
  };

  const openVideoModal = (videoUrl) => {
    setCurrentVideo(videoUrl);
    setShowVideoModal(true);
  };

  const handleScroll = (e) => {
    // Prevent the scroll event from propagating to parent elements
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check with dummy event
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [filteredCustomers]);

  // Add this new function to handle column resizing
  const handleColumnResize = (e, columnKey) => {
    if (!resizingColumn) return;

    const table = tableRef.current;
    if (!table) return;

    const th = table.querySelector(`th[data-column="${columnKey}"]`);
    if (!th) return;

    const rect = th.getBoundingClientRect();
    const newWidth = e.clientX - rect.left;
    
    // Set minimum width to prevent columns from becoming too narrow
    if (newWidth >= 100) {
      setColumnWidths(prev => ({
        ...prev,
        [columnKey]: newWidth
      }));
    }
  };

  // Add these event handlers for mouse events
  const handleMouseDown = (e, columnKey) => {
    setResizingColumn(columnKey);
    e.preventDefault();
  };

  const handleMouseUp = () => {
    setResizingColumn(null);
  };

  const handleMouseMove = (e) => {
    if (resizingColumn) {
      handleColumnResize(e, resizingColumn);
    }
  };

  // Add effect to handle mouse events
  useEffect(() => {
    if (resizingColumn) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingColumn]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Müşteri Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.addButton} onClick={() => {
                setSelectedCustomer(null);
                setIsModalOpen(true);
              }}>
                <FaUserPlus className={styles.buttonIcon} /> Yeni Müşteri Ekle
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{totalCustomers}</p>
                  <h3 className={styles.cardTitle}>Toplam Müşteri</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Müşteri</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Müşteri</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="İsim, bilgi veya vergi numarası ile ara (min. 3 karakter)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button
                  className={styles.clearSearch}
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
              {searchTerm && searchTerm.length > 0 && searchTerm.length < 3 && (
                <span className={styles.searchHint}>
                  En az 3 karakter girin
                </span>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedCustomer && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedCustomer['Başlık']}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedCustomer['Durum']?.toLowerCase() === 'aktif' 
                  ? styles.statusActive 
                  : styles.statusInactive
              }`}>
                {selectedCustomer['Durum'] || 'Belirtilmemiş'}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button className={`${styles.actionButton} ${styles.cardButton}`} onClick={handleCardClick}>
                <FaIdCard className={styles.actionIcon} />
                <span>Müşteri Kartı</span>
              </button>

              <button className={`${styles.actionButton} ${styles.editButton}`} onClick={handleEditClick}>
                <FaUserEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.statusButton}`} onClick={handleStatusChangeClick}>
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Durum Değiştir</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.addressButton}`} onClick={handleAddressClick}>
                <FaMapMarkerAlt className={styles.actionIcon} />
                <span>Adresler</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.personnelButton}`} onClick={handleAuthorizedPersonnelClick}>
                <FaUserFriends className={styles.actionIcon} />
                <span>Yetkililer</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          {/* Pagination Controls - filtre aktifken de görünür, çünkü sayfalama
              artık filtrelenmiş sonuç kümesi üzerinde çalışıyor */}
          {(
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                <span>
                  Sayfa {safePage} / {totalPages}
                  {isFilterActive
                    ? ` (${filteredCustomers.length} sonuç / ${totalCustomers} müşteri)`
                    : ` (${filteredCustomers.length} müşteri)`}
                </span>
                {isLoading && <span className={styles.loadingIndicator}> Yükleniyor...</span>}
              </div>
              <div className={styles.paginationControls}>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(1)}
                  disabled={safePage === 1 || isLoading}
                >
                  İlk
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={goToPreviousPage}
                  disabled={safePage === 1 || isLoading}
                >
                  <FaChevronLeft /> Önceki
                </button>
                <span className={styles.pageNumbers}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (safePage <= 3) {
                      pageNum = i + 1;
                    } else if (safePage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = safePage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        className={`${styles.pageNumber} ${safePage === pageNum ? styles.activePage : ''}`}
                        onClick={() => goToPage(pageNum)}
                        disabled={isLoading}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                <button
                  className={styles.paginationButton}
                  onClick={goToNextPage}
                  disabled={safePage === totalPages || isLoading}
                >
                  Sonraki <FaChevronRight />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => goToPage(totalPages)}
                  disabled={safePage === totalPages || isLoading}
                >
                  Son
                </button>
              </div>
            </div>
          )}

          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table 
                  ref={tableRef}
                  className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}
                  style={{ tableLayout: 'fixed' }}
                >
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th 
                            key={key}
                            data-column={key}
                            className={`${resizingColumn === key ? styles.resizing : styles.resizable}`}
                            style={{ width: columnWidths[key] || 'auto' }}
                            onMouseDown={(e) => handleMouseDown(e, key)}
                          >
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {pagedCustomers.length > 0 ? (
                      pagedCustomers.map((customer, index) => (
                        !customer.isEmpty && (
                          <tr 
                            key={customer.id} 
                            onClick={() => handleCustomerSelect(customer)}
                            className={selectedCustomer && selectedCustomer.id === customer.id ? styles.selectedRow : ''}
                          >
                            {Object.keys(columns).map(key => 
                              visibleColumns[key] && (
                                <td 
                                  key={key} 
                                  title={customer[key]}
                                  style={{ width: columnWidths[key] || 'auto' }}
                                >
                                  {key === 'Durum' ? (
                                    <span className={`${styles.statusBadge} ${
                                      customer['Durum'] === undefined || customer['Durum'] === null
                                        ? styles.statusNoData
                                        : customer['Durum']?.toLowerCase() === 'aktif'
                                          ? styles.statusActive
                                          : styles.statusInactive
                                    }`}>
                                      {customer['Durum'] === undefined || customer['Durum'] === null
                                        ? '-'
                                        : truncateText(customer['Durum'])}
                                    </span>
                                  ) : (
                                    truncateText(customer[key])
                                  )}
                                </td>
                              )
                            )}
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td 
                          colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} 
                          className={styles.noDataCell}
                          style={{ width: '100%' }}
                        >
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Filtrelere uygun müşteri bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {isCardModalOpen && selectedCustomer && (
        <CustomerCardModal
          customer={selectedCustomer}
          onClose={() => setIsCardModalOpen(false)}
          onEdit={() => {
            setIsCardModalOpen(false);
            setIsModalOpen(true);
          }}
        />
      )}

      {isModalOpen && (
        <EditCustomerModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          fetchCustomers={refreshCustomers}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
        />
      )}

      {isStatusModalOpen && (
        <ChangeCustomerState
          isStatusModalOpen={isStatusModalOpen}
          setIsStatusModalOpen={setIsStatusModalOpen}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          fetchCustomers={refreshCustomers}
        />
      )}

      {isAuthorizedPersonnelModalOpen && (
        <AuthorizedPersonnelModal
          isOpen={isAuthorizedPersonnelModalOpen}
          onClose={() => setIsAuthorizedPersonnelModalOpen(false)}
          selectedCustomer={selectedCustomer}
        />
      )}

      {isCustomerTariffsModalOpen && (
        <CustomerTariffsModal
          isOpen={isCustomerTariffsModalOpen}
          onClose={() => setIsCustomerTariffsModalOpen(false)}
          selectedCustomer={selectedCustomer}
        />
      )}

      {isAddressModalOpen && (
        <CustomerAddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          selectedCustomer={selectedCustomer}
        />
      )}

      {showVideoModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.videoModal}>
            <div className={styles.videoContent}>
              <video controls autoPlay>
                <source src={currentVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <button className={styles.closeButton} onClick={() => setShowVideoModal(false)}>
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default CustomerManagementPage;